import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowUpCircle, GraduationCap, Users, CheckCircle, XCircle, 
  AlertTriangle, ChevronRight, ChevronLeft, FileText, Search,
  RotateCcw, Award, UserMinus, Loader2, TrendingUp, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromotionPage = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('wizard');
  const [loading, setLoading] = useState(true);
  
  // Classes data
  const [classes, setClasses] = useState([]);
  const [alumni, setAlumni] = useState([]);
  const [alumniYears, setAlumniYears] = useState([]);
  const [selectedAlumniYear, setSelectedAlumniYear] = useState('all');
  const [alumniSearch, setAlumniSearch] = useState('');
  
  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [fromAcademicYear, setFromAcademicYear] = useState('2025-26');
  const [toAcademicYear, setToAcademicYear] = useState('2026-27');
  const [fromClassId, setFromClassId] = useState('');
  const [toClassId, setToClassId] = useState('');
  const [promotionMethod, setPromotionMethod] = useState('auto');
  const [promotionRules, setPromotionRules] = useState({
    min_percentage: 33,
    min_attendance: 75,
    max_failed_subjects: 2,
    all_subjects_pass_required: false
  });
  const [resetRollNumbers, setResetRollNumbers] = useState(true);
  
  // Students and preview
  const [eligibleStudents, setEligibleStudents] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  
  // Pass-out dialog
  const [passOutDialog, setPassOutDialog] = useState(false);
  const [passOutStudents, setPassOutStudents] = useState([]);
  const [generateCertificate, setGenerateCertificate] = useState(true);
  
  // Promote All state
  const [showPromoteAllDialog, setShowPromoteAllDialog] = useState(false);
  const [classMapping, setClassMapping] = useState(null);
  const [loadingMapping, setLoadingMapping] = useState(false);
  const [promotingAll, setPromotingAll] = useState(false);
  const [promoteAllResult, setPromoteAllResult] = useState(null);
  const [promoteAllAutoFees, setPromoteAllAutoFees] = useState(true);
  
  // Background job state for Promote All
  const [promoteJobId, setPromoteJobId] = useState(null);
  const [promoteJobProgress, setPromoteJobProgress] = useState(0);
  const [promoteJobMessage, setPromoteJobMessage] = useState('');
  const pollRef = useRef(null);
  
  // Get user and school_id
  const { user } = useAuth();

  useEffect(() => {
    fetchClasses();
    fetchAlumni();
  }, []);

  // Fetch class mapping for Promote All feature
  const fetchClassMapping = async () => {
    setLoadingMapping(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/promotion/class-mapping`,
        { headers: getAuthHeader() }
      );
      setClassMapping(response.data);
      setShowPromoteAllDialog(true);
    } catch (error) {
      console.error('Failed to fetch class mapping:', error);
      toast.error('Failed to load class mapping');
    } finally {
      setLoadingMapping(false);
    }
  };

  // Poll job status for Promote All
  const pollPromoteJobStatus = useCallback(async (jobId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/jobs/${jobId}/status`,
        { headers: getAuthHeader() }
      );
      
      const job = res.data;
      setPromoteJobProgress(job.progress || 0);
      setPromoteJobMessage(job.message || '');
      
      if (job.status === 'completed') {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setPromotingAll(false);
        setPromoteJobId(null);
        setPromoteAllResult(job.result);
        toast.success(job.message || 'Promotion completed!');
        fetchClasses();
        setShowPromoteAllDialog(false);
      } else if (job.status === 'failed') {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setPromotingAll(false);
        setPromoteJobId(null);
        toast.error(job.error || 'Promotion failed');
      }
    } catch (err) {
      console.error('Error polling job status:', err);
    }
  }, [getAuthHeader]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  // Promote All Students All Classes - Using Background Job
  const executePromoteAll = async () => {
    if (!classMapping || classMapping.total_students === 0) {
      toast.error('No students to promote');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to promote ALL ${classMapping.total_students} students from ALL classes?\n\nThis will:\n• Move each student to their next class\n• Students in highest class will be marked as "Passed Out"\n${promoteAllAutoFees ? '• Auto-assign fees for the new session' : ''}`)) {
      return;
    }
    
    setPromotingAll(true);
    setPromoteAllResult(null);
    setPromoteJobProgress(0);
    setPromoteJobMessage('Starting...');
    
    try {
      // Use background job for promotion
      const response = await axios.post(
        `${API_URL}/api/jobs/promote-all/${user?.school_id}`,
        {
          from_academic_year: fromAcademicYear,
          to_academic_year: toAcademicYear,
          auto_assign_fees: promoteAllAutoFees
        },
        { headers: getAuthHeader() }
      );
      
      const jobId = response.data.job_id;
      setPromoteJobId(jobId);
      
      // Start polling
      pollRef.current = setInterval(() => {
        pollPromoteJobStatus(jobId);
      }, 2000);
      
      // Initial poll
      await pollPromoteJobStatus(jobId);
      
    } catch (error) {
      console.error('Failed to start promotion job:', error);
      
      // Fallback to direct API call for non-background job endpoint
      try {
        const response = await axios.post(
          `${API_URL}/api/promotion/promote-all-classes`,
          {
            from_academic_year: fromAcademicYear,
            to_academic_year: toAcademicYear,
            auto_assign_fees: promoteAllAutoFees,
            skip_highest_class: true
          },
          { headers: getAuthHeader() }
        );
        setPromoteAllResult(response.data);
        toast.success(response.data.message);
        fetchClasses();
        setPromotingAll(false);
      } catch (fallbackError) {
        toast.error('Failed to promote students: ' + (fallbackError.response?.data?.detail || fallbackError.message));
        setPromotingAll(false);
      }
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/promotion/classes-for-promotion`,
        { headers: getAuthHeader() }
      );
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlumni = async () => {
    try {
      let url = `${API_URL}/api/promotion/alumni`;
      const params = [];
      if (selectedAlumniYear !== 'all') params.push(`year=${selectedAlumniYear}`);
      if (alumniSearch) params.push(`search=${alumniSearch}`);
      if (params.length) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, { headers: getAuthHeader() });
      setAlumni(response.data.alumni);
      setAlumniYears(response.data.years);
    } catch (error) {
      console.error('Failed to fetch alumni:', error);
    }
  };

  useEffect(() => {
    fetchAlumni();
  }, [selectedAlumniYear, alumniSearch]);

  // Reset Promotion Status Function
  const resetPromotionStatus = async () => {
    const academicYear = window.prompt('Enter academic year to reset (e.g., 2026-2027):', toAcademicYear);
    if (!academicYear) return;
    
    try {
      // Preview first
      toast.info('Checking...');
      const previewRes = await axios.get(
        `${API_URL}/api/promotion/reset-promotion-status/preview?academic_year=${encodeURIComponent(academicYear)}`,
        { headers: getAuthHeader() }
      );
      
      const { students_to_reset, invoices_to_delete } = previewRes.data;
      
      if (students_to_reset === 0) {
        toast.info(`No promoted students found for ${academicYear}`);
        return;
      }
      
      const confirmMsg = `Found:\n• ${students_to_reset} promoted students\n• ${invoices_to_delete} pending invoices\n\nReset promotion status and delete invoices?`;
      
      if (!window.confirm(confirmMsg)) return;
      
      // Execute reset
      toast.info('Resetting...');
      const res = await axios.post(
        `${API_URL}/api/promotion/reset-promotion-status?academic_year=${encodeURIComponent(academicYear)}&delete_invoices=true`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (res.data.success) {
        toast.success(`Reset ${res.data.students_reset} students and deleted ${res.data.invoices_deleted} invoices. You can now promote again!`);
        fetchClasses();
      } else {
        toast.error('Failed: ' + (res.data.errors?.join(', ') || 'Unknown error'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset promotion status');
    }
  };

  const fetchEligibleStudents = async () => {
    if (!fromClassId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/promotion/eligible-students/${fromClassId}`,
        { headers: getAuthHeader() }
      );
      setEligibleStudents(response.data);
      // Select all eligible by default
      const eligible = response.data.students.filter(s => s.eligibility.eligible);
      setSelectedStudents(eligible.map(s => s.id));
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const previewPromotion = async () => {
    if (!fromClassId || !toClassId) {
      toast.error('Please select both classes');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/promotion/preview`,
        {
          from_academic_year: fromAcademicYear,
          to_academic_year: toAcademicYear,
          from_class_id: fromClassId,
          to_class_id: toClassId,
          promotion_method: promotionMethod,
          rules: promotionMethod === 'auto' ? promotionRules : null,
          student_ids: promotionMethod === 'manual' ? selectedStudents : null,
          reset_roll_numbers: resetRollNumbers
        },
        { headers: getAuthHeader() }
      );
      setPreviewData(response.data);
      setWizardStep(4);
    } catch (error) {
      toast.error('Failed to preview promotion');
    } finally {
      setLoading(false);
    }
  };

  const executePromotion = async () => {
    setExecuting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/promotion/execute`,
        {
          from_academic_year: fromAcademicYear,
          to_academic_year: toAcademicYear,
          from_class_id: fromClassId,
          to_class_id: toClassId,
          promotion_method: promotionMethod,
          rules: promotionMethod === 'auto' ? promotionRules : null,
          student_ids: promotionMethod === 'manual' ? selectedStudents : null,
          reset_roll_numbers: resetRollNumbers,
          auto_assign_fees: true  // CRITICAL: Auto-generate invoices on promotion
        },
        { headers: getAuthHeader() }
      );
      setExecutionResult(response.data);
      setWizardStep(5);
      toast.success('Promotion completed successfully!');
      fetchClasses();
    } catch (error) {
      toast.error('Failed to execute promotion');
    } finally {
      setExecuting(false);
    }
  };

  const markPassOut = async () => {
    if (passOutStudents.length === 0) {
      toast.error('No students selected');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/promotion/pass-out`,
        {
          student_ids: passOutStudents,
          academic_year: toAcademicYear,
          generate_certificate: generateCertificate,
          certificate_type: 'school_leaving'
        },
        { headers: getAuthHeader() }
      );
      toast.success(`${response.data.passed_out_count} students marked as passed out`);
      setPassOutDialog(false);
      setPassOutStudents([]);
      fetchClasses();
      fetchAlumni();
    } catch (error) {
      toast.error('Failed to mark students as passed out');
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = (students) => {
    setSelectedStudents(students.map(s => s.id));
  };

  const deselectAllStudents = () => {
    setSelectedStudents([]);
  };

  const resetWizard = () => {
    setWizardStep(1);
    setFromClassId('');
    setToClassId('');
    setEligibleStudents(null);
    setPreviewData(null);
    setSelectedStudents([]);
    setExecutionResult(null);
  };

  const renderWizardSteps = () => {
    const steps = [
      { num: 1, title: 'Academic Year', icon: Clock },
      { num: 2, title: 'Select Classes', icon: GraduationCap },
      { num: 3, title: 'Rules & Students', icon: Users },
      { num: 4, title: 'Preview', icon: FileText },
      { num: 5, title: 'Complete', icon: CheckCircle }
    ];

    return (
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <div key={step.num} className="flex items-center">
            <div className={`flex items-center gap-2 ${
              wizardStep >= step.num ? 'text-blue-600' : 'text-gray-400'
            }`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                wizardStep > step.num 
                  ? 'bg-green-500 text-white' 
                  : wizardStep === step.num 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700'
              }`}>
                {wizardStep > step.num ? <CheckCircle className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
              </div>
              <span className="hidden md:block text-sm font-medium">{step.title}</span>
            </div>
            {index < steps.length - 1 && (
              <ChevronRight className="h-5 w-5 mx-2 text-gray-300" />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout title="Student Promotion">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {classes.slice(0, 4).map(cls => (
            <Card key={cls.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                    <GraduationCap className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{cls.name}</p>
                    <p className="text-xl font-bold">{cls.student_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* PROMOTE ALL STUDENTS BUTTON */}
        <Card className="border-2 border-green-500 bg-green-50 dark:bg-green-950/30">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-500 p-3 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-700 dark:text-green-300">
                    Promote All Students (All Classes)
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    One-click promotion: All students from all classes will be promoted to next class
                  </p>
                </div>
              </div>
              <Button 
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={fetchClassMapping}
                disabled={loadingMapping}
                data-testid="promote-all-btn"
              >
                {loadingMapping ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <ArrowUpCircle className="h-5 w-5 mr-2" />
                )}
                Promote All Students
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Promote All Dialog */}
        <Dialog open={showPromoteAllDialog} onOpenChange={setShowPromoteAllDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <TrendingUp className="h-5 w-5" />
                Promote All Students - All Classes
              </DialogTitle>
            </DialogHeader>
            
            {classMapping && (
              <div className="space-y-4">
                {/* Academic Years */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>From Academic Year</Label>
                    <Input 
                      value={fromAcademicYear} 
                      onChange={(e) => setFromAcademicYear(e.target.value)}
                      placeholder="2025-26"
                    />
                  </div>
                  <div>
                    <Label>To Academic Year</Label>
                    <Input 
                      value={toAcademicYear} 
                      onChange={(e) => setToAcademicYear(e.target.value)}
                      placeholder="2026-27"
                    />
                  </div>
                </div>
                
                {/* Auto Fees Option */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Auto-assign Fees</p>
                    <p className="text-sm text-muted-foreground">Automatically create invoices for promoted students</p>
                  </div>
                  <Switch 
                    checked={promoteAllAutoFees} 
                    onCheckedChange={setPromoteAllAutoFees}
                  />
                </div>
                
                {/* Class Mapping Preview */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 dark:bg-gray-800 p-3 font-medium">
                    Class Promotion Mapping (Total: {classMapping.total_students} students)
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>From Class</TableHead>
                        <TableHead>To Class</TableHead>
                        <TableHead className="text-right">Students</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {classMapping.mapping?.map((m, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{m.from_class_name}</TableCell>
                          <TableCell>
                            <Badge variant={m.to_class_name === "Pass Out / Alumni" ? "destructive" : "default"}>
                              {m.to_class_name}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-bold">{m.student_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Progress Bar - Shows during promotion */}
                {promotingAll && promoteJobId && (
                  <div className="p-4 border-2 border-blue-500 rounded bg-blue-50 dark:bg-blue-900/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      <h5 className="font-bold text-blue-700">Promoting Students...</h5>
                    </div>
                    <Progress value={promoteJobProgress} className="h-3 mb-2" />
                    <p className="text-sm text-blue-600">{promoteJobMessage || 'Processing...'} ({promoteJobProgress}%)</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      এই প্রক্রিয়া Background-এ চলছে। Page refresh করলেও চলতে থাকবে।
                    </p>
                  </div>
                )}

                {/* Results */}
                {promoteAllResult && (
                  <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-950/30">
                    <h4 className="font-bold text-green-700 dark:text-green-300 mb-2">
                      Promotion Complete!
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-green-600">{promoteAllResult.promoted}</p>
                        <p className="text-sm text-muted-foreground">Promoted</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-orange-600">{promoteAllResult.passed_out}</p>
                        <p className="text-sm text-muted-foreground">Passed Out</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-600">{promoteAllResult.skipped}</p>
                        <p className="text-sm text-muted-foreground">Skipped</p>
                      </div>
                    </div>
                    
                    {promoteAllResult.errors?.length > 0 && (
                      <div className="mt-3 p-2 bg-red-50 dark:bg-red-950/30 rounded text-sm">
                        <p className="font-medium text-red-600">Errors ({promoteAllResult.errors.length}):</p>
                        <ul className="text-red-500 text-xs max-h-32 overflow-y-auto">
                          {promoteAllResult.errors.slice(0, 10).map((e, i) => (
                            <li key={i}>• {e}</li>
                          ))}
                          {promoteAllResult.errors.length > 10 && (
                            <li>... and {promoteAllResult.errors.length - 10} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPromoteAllDialog(false)}>
                Close
              </Button>
              {!promoteAllResult && (
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={executePromoteAll}
                  disabled={promotingAll || !classMapping || classMapping.total_students === 0}
                  data-testid="execute-promote-all-btn"
                >
                  {promotingAll ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Promoting...
                    </>
                  ) : (
                    <>
                      <ArrowUpCircle className="h-4 w-4 mr-2" />
                      Promote All {classMapping?.total_students || 0} Students
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <CardTitle>Student Promotion Management</CardTitle>
            <CardDescription>Promote students to next class based on exam results</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="wizard" className="gap-2">
                  <ArrowUpCircle className="h-4 w-4" /> Promotion Wizard
                </TabsTrigger>
                <TabsTrigger value="alumni" className="gap-2">
                  <Award className="h-4 w-4" /> Alumni / Pass-Out
                </TabsTrigger>
              </TabsList>

              {/* Promotion Wizard */}
              <TabsContent value="wizard">
                {renderWizardSteps()}

                {/* Important Flow Instructions */}
                {wizardStep === 1 && (
                  <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Important: Follow This Sequence!</h4>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-amber-700 dark:text-amber-300">
                          <li><strong>First:</strong> Go to Settings → Academic Year → Set the NEW session as "Current" (e.g., 2026-2027)</li>
                          <li><strong>Second:</strong> Create Fee Structures for the NEW academic year</li>
                          <li><strong>Third:</strong> Ensure Fee Heads have <span className="font-bold text-green-600">"Promotion"</span> checkbox enabled (Fee Setup → Edit Fee Head)</li>
                          <li><strong>Fourth:</strong> Come back here and promote students → Invoices will be auto-generated!</li>
                        </ol>
                        <div className="mt-3 pt-3 border-t border-amber-300">
                          <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                            ⚠️ Already promoted with wrong settings? Reset and try again:
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={resetPromotionStatus}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" /> Reset Promotion Status
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 1: Academic Year */}
                {wizardStep === 1 && (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>From Academic Year</Label>
                        <Select value={fromAcademicYear} onValueChange={setFromAcademicYear}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2024-25">2024-25</SelectItem>
                            <SelectItem value="2025-26">2025-26</SelectItem>
                            <SelectItem value="2026-27">2026-27</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>To Academic Year</Label>
                        <Select value={toAcademicYear} onValueChange={setToAcademicYear}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2025-26">2025-26</SelectItem>
                            <SelectItem value="2026-27">2026-27</SelectItem>
                            <SelectItem value="2027-28">2027-28</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button onClick={() => setWizardStep(2)}>
                        Next <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Select Classes */}
                {wizardStep === 2 && (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>From Class (Source)</Label>
                        <Select value={fromClassId} onValueChange={setFromClassId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select source class" />
                          </SelectTrigger>
                          <SelectContent>
                            {classes.map(cls => (
                              <SelectItem key={cls.id} value={cls.id}>
                                {cls.name} ({cls.student_count} students)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>To Class (Target)</Label>
                        <Select value={toClassId} onValueChange={setToClassId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select target class" />
                          </SelectTrigger>
                          <SelectContent>
                            {classes.filter(c => c.id !== fromClassId).map(cls => (
                              <SelectItem key={cls.id} value={cls.id}>
                                {cls.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="flex justify-between">
                      <Button variant="outline" onClick={() => setWizardStep(1)}>
                        <ChevronLeft className="h-4 w-4 mr-2" /> Back
                      </Button>
                      <Button 
                        onClick={() => {
                          fetchEligibleStudents();
                          setWizardStep(3);
                        }}
                        disabled={!fromClassId || !toClassId}
                      >
                        Next <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Rules & Students */}
                {wizardStep === 3 && (
                  <div className="space-y-6">
                    {/* Promotion Method */}
                    <div className="space-y-4">
                      <Label>Promotion Method</Label>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Card 
                          className={`cursor-pointer transition-all ${promotionMethod === 'auto' ? 'ring-2 ring-blue-500' : ''}`}
                          onClick={() => setPromotionMethod('auto')}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <TrendingUp className="h-8 w-8 text-blue-500" />
                              <div>
                                <p className="font-semibold">Auto (Result-Based)</p>
                                <p className="text-sm text-muted-foreground">Promote based on exam results</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card 
                          className={`cursor-pointer transition-all ${promotionMethod === 'manual' ? 'ring-2 ring-blue-500' : ''}`}
                          onClick={() => setPromotionMethod('manual')}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <Users className="h-8 w-8 text-green-500" />
                              <div>
                                <p className="font-semibold">Manual Selection</p>
                                <p className="text-sm text-muted-foreground">Select students manually</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>

                    {/* Auto Rules */}
                    {promotionMethod === 'auto' && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Promotion Rules</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Minimum Percentage to Pass (%)</Label>
                              <Input 
                                type="number"
                                value={promotionRules.min_percentage}
                                onChange={(e) => setPromotionRules({
                                  ...promotionRules,
                                  min_percentage: parseFloat(e.target.value)
                                })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Minimum Attendance (%)</Label>
                              <Input 
                                type="number"
                                value={promotionRules.min_attendance}
                                onChange={(e) => setPromotionRules({
                                  ...promotionRules,
                                  min_attendance: parseFloat(e.target.value)
                                })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Max Failed Subjects Allowed</Label>
                              <Input 
                                type="number"
                                value={promotionRules.max_failed_subjects}
                                onChange={(e) => setPromotionRules({
                                  ...promotionRules,
                                  max_failed_subjects: parseInt(e.target.value)
                                })}
                              />
                            </div>
                            <div className="flex items-center gap-3 pt-6">
                              <Switch 
                                checked={promotionRules.all_subjects_pass_required}
                                onCheckedChange={(checked) => setPromotionRules({
                                  ...promotionRules,
                                  all_subjects_pass_required: checked
                                })}
                              />
                              <Label>All Subjects Pass Required</Label>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Student List for Manual Selection */}
                    {promotionMethod === 'manual' && eligibleStudents && (
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">Select Students to Promote</CardTitle>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => selectAllStudents(eligibleStudents.students)}>
                                Select All
                              </Button>
                              <Button size="sm" variant="outline" onClick={deselectAllStudents}>
                                Deselect All
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="max-h-[400px] overflow-y-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-12">Select</TableHead>
                                  <TableHead>Name</TableHead>
                                  <TableHead>Roll No</TableHead>
                                  <TableHead>Marks %</TableHead>
                                  <TableHead>Attendance</TableHead>
                                  <TableHead>Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {eligibleStudents.students.map(student => (
                                  <TableRow key={student.id}>
                                    <TableCell>
                                      <Checkbox 
                                        checked={selectedStudents.includes(student.id)}
                                        onCheckedChange={() => toggleStudentSelection(student.id)}
                                      />
                                    </TableCell>
                                    <TableCell className="font-medium">{student.name}</TableCell>
                                    <TableCell>{student.roll_number || '-'}</TableCell>
                                    <TableCell>
                                      {student.eligibility.result.has_results 
                                        ? `${student.eligibility.result.percentage}%` 
                                        : 'No data'}
                                    </TableCell>
                                    <TableCell>{student.eligibility.attendance}%</TableCell>
                                    <TableCell>
                                      {student.eligibility.eligible ? (
                                        <Badge className="bg-green-500">Eligible</Badge>
                                      ) : (
                                        <Badge variant="destructive">Not Eligible</Badge>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          <p className="mt-4 text-sm text-muted-foreground">
                            Selected: {selectedStudents.length} / {eligibleStudents.students.length} students
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Options */}
                    <div className="flex items-center gap-3">
                      <Switch 
                        checked={resetRollNumbers}
                        onCheckedChange={setResetRollNumbers}
                      />
                      <Label>Reset Roll Numbers in New Class</Label>
                    </div>

                    <div className="flex justify-between">
                      <Button variant="outline" onClick={() => setWizardStep(2)}>
                        <ChevronLeft className="h-4 w-4 mr-2" /> Back
                      </Button>
                      <Button onClick={previewPromotion} disabled={loading}>
                        {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Preview <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 4: Preview */}
                {wizardStep === 4 && previewData && (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-blue-600">{previewData.summary.total_students}</p>
                          <p className="text-sm text-muted-foreground">Total Students</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50 dark:bg-green-900/20 border-green-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-green-600">{previewData.summary.to_promote}</p>
                          <p className="text-sm text-muted-foreground">Will Be Promoted</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-red-600">{previewData.summary.to_retain}</p>
                          <p className="text-sm text-muted-foreground">Will Be Retained</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Promotion Details */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Promotion Details</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-4 mb-4">
                          <Badge variant="outline" className="text-lg px-4 py-2">
                            {previewData.from_class?.name}
                          </Badge>
                          <ArrowUpCircle className="h-6 w-6 text-green-500" />
                          <Badge className="bg-green-500 text-lg px-4 py-2">
                            {previewData.to_class?.name}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Academic Year: {fromAcademicYear} → {toAcademicYear}
                        </p>
                      </CardContent>
                    </Card>

                    {/* Students to Retain */}
                    {previewData.to_retain.length > 0 && (
                      <Card className="border-red-200">
                        <CardHeader>
                          <CardTitle className="text-lg text-red-600 flex items-center gap-2">
                            <XCircle className="h-5 w-5" /> Students to Retain ({previewData.to_retain.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Roll No</TableHead>
                                <TableHead>Marks</TableHead>
                                <TableHead>Attendance</TableHead>
                                <TableHead>Reason</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {previewData.to_retain.map(student => (
                                <TableRow key={student.id}>
                                  <TableCell className="font-medium">{student.name}</TableCell>
                                  <TableCell>{student.roll_number}</TableCell>
                                  <TableCell>{student.result?.percentage || 0}%</TableCell>
                                  <TableCell>{student.attendance}%</TableCell>
                                  <TableCell>
                                    {student.reasons?.map((r, i) => (
                                      <Badge key={i} variant="outline" className="mr-1 text-red-600">
                                        {r}
                                      </Badge>
                                    ))}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    )}

                    <div className="flex justify-between">
                      <Button variant="outline" onClick={() => setWizardStep(3)}>
                        <ChevronLeft className="h-4 w-4 mr-2" /> Back
                      </Button>
                      <Button 
                        onClick={executePromotion} 
                        disabled={executing}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {executing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Execute Promotion
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 5: Complete */}
                {wizardStep === 5 && executionResult && (
                  <div className="text-center space-y-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-green-600">Promotion Completed!</h2>
                    
                    <div className="grid md:grid-cols-2 gap-4 max-w-md mx-auto">
                      <Card className="bg-green-50 dark:bg-green-900/20">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-green-600">{executionResult.promoted_count}</p>
                          <p className="text-sm">Students Promoted</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-amber-50 dark:bg-amber-900/20">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-amber-600">{executionResult.retained_count}</p>
                          <p className="text-sm">Students Retained</p>
                        </CardContent>
                      </Card>
                    </div>

                    <Button onClick={resetWizard}>
                      <RotateCcw className="h-4 w-4 mr-2" /> Start New Promotion
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* Alumni Tab */}
              <TabsContent value="alumni">
                <div className="space-y-6">
                  {/* Actions */}
                  <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="flex gap-4">
                      <Select value={selectedAlumniYear} onValueChange={setSelectedAlumniYear}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Filter by year" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Years</SelectItem>
                          {alumniYears.map(year => (
                            <SelectItem key={year} value={year}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input 
                          placeholder="Search alumni..."
                          className="pl-10 w-[250px]"
                          value={alumniSearch}
                          onChange={(e) => setAlumniSearch(e.target.value)}
                        />
                      </div>
                    </div>
                    <Button onClick={() => setPassOutDialog(true)}>
                      <UserMinus className="h-4 w-4 mr-2" /> Mark Pass-Out
                    </Button>
                  </div>

                  {/* Alumni List */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Admission No</TableHead>
                        <TableHead>Final Class</TableHead>
                        <TableHead>Pass-Out Year</TableHead>
                        <TableHead>Contact</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alumni.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            No alumni records found
                          </TableCell>
                        </TableRow>
                      ) : (
                        alumni.map(a => (
                          <TableRow key={a.id}>
                            <TableCell className="font-medium">{a.name}</TableCell>
                            <TableCell><code className="bg-muted px-2 py-1 rounded">{a.admission_number}</code></TableCell>
                            <TableCell>{a.final_class}</TableCell>
                            <TableCell><Badge variant="outline">{a.pass_out_year}</Badge></TableCell>
                            <TableCell className="text-sm text-muted-foreground">{a.email || a.phone || '-'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Pass-Out Dialog */}
      <Dialog open={passOutDialog} onOpenChange={setPassOutDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Mark Students as Passed Out</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Final Year Class</Label>
              <Select onValueChange={(classId) => {
                const cls = classes.find(c => c.id === classId);
                if (cls) {
                  // Fetch students for this class
                  axios.get(`${API_URL}/api/promotion/eligible-students/${classId}`, { headers: getAuthHeader() })
                    .then(res => {
                      setPassOutStudents(res.data.students.map(s => s.id));
                    });
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name} ({cls.student_count} students)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Switch 
                checked={generateCertificate}
                onCheckedChange={setGenerateCertificate}
              />
              <Label>Generate School Leaving Certificate</Label>
            </div>

            {passOutStudents.length > 0 && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{passOutStudents.length} students will be marked as passed out</p>
                <p className="text-sm text-muted-foreground">They will be moved to Alumni list</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPassOutDialog(false)}>Cancel</Button>
            <Button onClick={markPassOut} disabled={passOutStudents.length === 0}>
              <Award className="h-4 w-4 mr-2" /> Mark as Passed Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default PromotionPage;
